import {
  CUSTOM_QUESTION_ID_REG,
  CUSTOM_QUESTION_REG,
  EXTENDED_QUESTION_ID_REG,
  R_TYPES,
  TRAIT_QUESTION_TYPES,
  TRAIT_TYPES,
  TURF_BATCH_TYPES,
  VERBATIM_OWN_TYPES,
  VERBATIM_TYPES,
  WC_MIN_ITEMS,
  WC_QUESTION_TYPES,
  WC_TYPES
} from '@aytm/app/stats/page/components/lib/constants'
import PageGlobals from '@aytm/app/stats/lib/page/page-globals'
import { clipText, decodeText, encodeText } from '@aytm/app/stats/lib/util/helpers'
import { measureTextWidth } from '@aytm/app/stats/lib/util/dom'

export const getSurvey = (survey = {}) =>
  0 < Object.keys(survey).length && survey.questions ? survey : window.survey || {}

export const getQuestionId = (question, parentID = null) =>
  0 < question.position
    ? `${(question.pre_type && 'pq') || 'q'}${question.visual_position}`
    : 'sn' != question.type && 0 < question.visual_position && null !== parentID
    ? `${parentID.toUpperCase()}_${question.visual_position}`
    : question.id

// The dom id a batch row carries, so the page can scroll straight to a batch instead of walking
// the view objects the blocks no longer keep.
export const batchElementId = (questionId, position) => `q-${questionId}-b-${position}`

export const getQuestionIcon = question =>
  Object.hasOwn(question, 'variant')
    ? PageGlobals.QUESTION_TYPE_ICONS[question.variant]
    : 'mp' == question.type
    ? 'radios' == question.subType
      ? `${PageGlobals.QUESTION_TYPE_ICONS[question.type]}-rb`
      : `${PageGlobals.QUESTION_TYPE_ICONS[question.type]}-cb`
    : PageGlobals.QUESTION_TYPE_ICONS[question.type]

// Q/PQ*visual_position* -> position
// An id is `Q{visual_position}` -- the number the reader sees -- so the digits are looked up as a
// visual position, and the prefix tells a pre-question apart from a regular one carrying the same
// number. An id naming a question the survey does not hold comes back as it went in, the way an
// unparseable one does: a caller cannot tell a wrong answer from a right one, but it can tell that
// nothing was resolved.
export const questionIdToPosition = (id, mode, survey = {}) => {
  const surveyObj = getSurvey(survey)
  const m = id.match(/^(p?q)(\d+)$/i)
  if (null === m) return id
  switch (mode) {
    case 1:
      if ('pq' == m[1]) {
        return surveyObj.questions.find(q => q.pre_type && q.position == Number(m[2])).position
      } else {
        const pq_length = surveyObj.questions.filter(q => q.pre_type).length
        return surveyObj.questions.find(q => !q.pre_type && q.position == pq_length + Number(m[2])).position
      }
    default: {
      const isPre = 'pq' === m[1].toLowerCase()
      const question = surveyObj.questions?.find(
        q => Boolean(q.pre_type) === isPre && q.visual_position == Number(m[2])
      )

      return question ? question.position : id
    }
  }
}

// position -> q/pq*visual_position*
// The id: `q|pq{visual_position}`, built from the number the reader sees. A position the survey does
// not hold comes back as it went in, like everywhere else in this family -- a caller that cannot
// resolve a question needs to carry on, not to be thrown out of whatever it was rendering.
export const questionPositionToId = (position, survey = {}) => {
  if (Number.isNaN(Number(position))) return position

  const question = getSurvey(survey).questions?.find(q => q.position == Number(position))
  if (!question) return position

  return `${question.pre_type != undefined ? 'pq' : 'q'}${question.visual_position}`
}

// The OLD id: `q{position}`, built from where the question is stored rather than from the number the
// reader sees. Kept apart from `questionPositionToId`, which builds `q{visual_position}` -- the two
// spellings are what this family exists to convert between, so neither name may stand for both.
export const questionPositionToOldId = (position, survey = {}) => {
  if (Number.isNaN(Number(position))) return position

  const question = getSurvey(survey).questions?.find(q => q.position == Number(position))

  return question ? `q${question.position}` : position
}

export const questionPositionToVP = (position, survey = {}) => {
  const surveyObj = getSurvey(survey)
  const question = surveyObj.questions.find(q => q.position == position)
  return question.visual_position
}

// q*position* -> q/pq*visual_position*
export const questionOldIdToId = (oldId, survey = {}) => {
  if (oldId && CUSTOM_QUESTION_REG.test(oldId)) return oldId
  const m = Number(oldId.slice(1))
  if (Number.isNaN(m)) return null
  // a key left behind by a question the survey no longer holds resolves to nothing, like a malformed one
  if (!getSurvey(survey).questions?.some(q => q.position == m)) return null
  return questionPositionToId(m, survey)
}

export const hasComments = items => {
  if (!Array.isArray(items)) return false
  return (
    0 < items.length &&
    items.some(
      item =>
        null !== item && (item.comments || (Object.hasOwn(item, 'answerVariants') && hasComments(item.answerVariants)))
    )
  )
}

export const processTextString = (text, options = {}) => {
  let newText = (text !== void 0 && text != null && String(text)) || ''
  if (!(options != null ? options.ignoreLogic : void 0)) {
    if (options != null ? options.removeLogic : void 0) {
      newText = removeLogicFromText(newText, options)
    } else {
      const qLogicClass = 'custom-logic'
      const qPipingClass = 'custom-logic-piping'
      const qAliasesClass = 'custom-logic-alias'

      newText = newText
        .replace(/(\[(?!\[|@)(?![A-Z][A-Z0-9_]*])[^\[\]]*\])(?!\])/gi, '<span class="' + qLogicClass + '">$1</span>') // eslint-disable-line no-useless-escape
        .replace(
          /(\[(?!\[|@)(?!(?:entity|attribute)])([A-Z][A-Z0-9_]+)\])(?!\])/gi, // eslint-disable-line no-useless-escape
          '<span class="' + qLogicClass + '">$1</span><span class="' + qPipingClass + '">[&hellip;]</span>'
        )
        .replace(/(\[(?!\[)@[^\[\]]+\])(?!\])/gi, '<span class="' + qAliasesClass + '">$1</span>') // eslint-disable-line no-useless-escape
        .replace(/\[(\[[^\[\]]+\])\]/gi, '$1') // eslint-disable-line no-useless-escape
    }
  }
  return newText
}

export const processText = (text, options = null) => {
  if (Array.isArray(text)) {
    return text.map(str => processTextString(str, options))
  } else {
    return processTextString(text, options)
  }
}

// --- Question and answer text ------------------------------------------------------------------
// The one set of text helpers the page has: the coffee page.lib methods of the same names call these
// through window.$STATS, so a label reads the same whichever side draws it.

// Numeric character references (`&#39;`) back to the characters they name.
export const decodeHtmlCharCodes = (text = '') =>
  (null != text ? String(text) : '').replace(/&#(\d+);/g, (match, charCode) => String.fromCharCode(charCode))

// Cut to `limit` with an ellipsis, keeping whole characters, so a label never ends on half an emoji
// or a letter without its accent. The tag options are removeTagsFromText's.
export const truncateText = (text, limit = 30, options = {}) => {
  let result = decodeText(removeTagsFromText(text, options))
  result = limit < result.length ? clipText(result, limit - 3) + '...' : result
  return encodeText(result)
}

// Authored text is shown as its words and never run: a script element loses its tags and keeps its
// content, multi-line content included.
export const removeScriptsFromText = (text = '') =>
  (null != text ? String(text) : '').replace(/<script\b[^>]*>/gi, '').replace(/<\/script\s*>/gi, '')

// Drops the inline formatting a question's own words carry, keeping the words; script tags always go.
// A caller that draws the formatting keeps it by turning its flag off.
export const removeTagsFromText = (
  text = '',
  { removeB = true, removeI = true, removeU = true, removeBR = true, decode = false } = {}
) => {
  let newText = removeScriptsFromText(text)
  if (removeB) newText = newText.replace(/<\/?b>/gi, '')
  if (removeI) newText = newText.replace(/<\/?i>/gi, '')
  if (removeU) newText = newText.replace(/<\/?u>/gi, '')
  if (removeBR) newText = newText.replace(/<br\s*\/?>/gi, '')
  return decode ? decodeHtmlCharCodes(newText) : newText
}

// Logic markers (`[Q1A2]`, `[ENTITY]`) go; `[@alias]` pipes go too unless `removeAliases` is off, and an
// escaped `[[text]]` reads as `[text]`.
export const removeLogicFromText = (text = '', { removeAliases = true } = {}) => {
  let newText = (null != text ? String(text) : '')
    .replace(/(\[(?!\[|@)(?![a-z]\w*])[^[\]]*])(?!])/gi, '')
    .replace(/(\[(?!\[|@)(?!(?:entity|attribute)])([a-z]\w+)])(?!])/gi, '')
  if (removeAliases) newText = newText.replace(/(\[(?!\[)@[^[\]]+])(?!])/gi, '')
  return newText.replace(/\[(\[[^[\]]+])]/gi, '$1')
}

// The brackets the cut encoded come back as brackets: the result is read as text as well as markup,
// and `&lbrack;` read as text shows the entity.
export const removeLogicAndTruncate = (text, limit = 30, options = {}) =>
  truncateText(removeLogicFromText(text, options), limit, options)
    .replace(/&lbrack;/g, '[')
    .replace(/&rbrack;/g, ']')

export const noTextPlaceholder = (el, label = '', position = '') => {
  if (!Object.hasOwn(el, 'image') && !/^\[asset.*im_/i.test(el.text)) return ''
  return (label || (Object.hasOwn(el, 'answerVariants') ? 'B' : 'A')) + (position || el.position)
}

const LABEL_FONT = '12px Ubuntu, Calibri, Segoe, Helvetica, Arial'

// Cut a label to the width of `l` digits in the chart font. Measured on a canvas, so no label lays the
// report out: in-page measuring cost a full layout per character.
export const transformText = (rawText, l = 40) => {
  const text = decodeText(removeTagsFromText(removeLogicFromText(rawText)))

  const limit = measureTextWidth('1'.repeat(parseInt(l, 10)), LABEL_FONT)
  if (null === limit) return encodeText(text, false)

  // measured as the page lays text out: whitespace runs count once and none counts at either end, in
  // whole pixels, so the cut lands on the same character as it does on the page
  const laidOut = index => text.slice(0, index).replace(/\s+/g, ' ').trim()
  const fits = index => Math.round(measureTextWidth(laidOut(index), LABEL_FONT)) <= Math.round(limit)

  let index = 0
  while (index < text.length && fits(index)) index++

  return encodeText(index < text.length ? clipText(text, index - 3) + '...' : text, false)
}

// Mirrors the page-lib helper. That version also accepts an explicit prefix to test against;
// no caller here needs it, so only the default form is ported.
export const isCustomQuestion = (id, prefix) =>
  undefined === prefix ? CUSTOM_QUESTION_ID_REG.test(id) : new RegExp(`^${prefix}(\\d+)(?:[_](\\d+))?$`, 'i').test(id)

// A block draws its chart only where nothing has put the report into a map, or into no chart at
// all -- and either the question's own mode or the report-wide one can do that, so both are asked.
// Pass whichever the caller holds; a mode it does not know about answers for nothing.

// A question may pin its own decimal precision; otherwise the report-wide setting applies. What
// counts as pinned is a usable number, not the mere presence of the key: a question that carries
// it empty has not pinned anything, and reading that as an override handed the cell a value it
// could not draw with, so it fell back to a decimal the reader never chose and the setting looked
// ignored on that question alone. Zero is a pin like any other.
export const resolvePercentDecimals = (format, reportPercentDecimals) =>
  Number.isFinite(format?.percentDecimals) ? format.percentDecimals : reportPercentDecimals

// Answer labels open the filters panel unless the question format (or the mounting view) opts out.
export const isClickable = (format, fallback) => {
  if (undefined !== format?.clickable) return format.clickable
  if (undefined !== fallback) return fallback

  return true
}

// Whether the question offers a TURF analysis: a multi-select, or a matrix-family question with
// something to combine across.
export const isTurfType = ({ type, variant, subType, answerVariants = [], batches = [] } = {}) => {
  if ('cb' === variant && 1 < answerVariants.length) return true
  if ('cb' === type || (TURF_BATCH_TYPES.includes(type) && 2 <= batches.length)) return true

  return 'mp' === type && 'default' === subType
}

// Whether the question can be split by a demographic trait. A format that ignores traits opts
// out regardless of the question type.
export const isTraitType = ({ format, type, variant, questionType } = {}) => {
  if (true === format?.ignoreTraits) return false

  return TRAIT_QUESTION_TYPES.includes(questionType) || TRAIT_TYPES.includes(variant || type)
}

// Whether the question reports respondent counts rather than answer counts.
export const isRType = ({ type, variant, questionType, questionSubType, subType, ignoreRType } = {}) => {
  if (ignoreRType) return false
  if ('mp' === questionType && 'default' === questionSubType) return true
  if ('mp' === (variant || type) && 'default' === subType) return true

  return R_TYPES.includes(variant || type)
}

// Whether a word cloud is worth offering: a supported question type carrying enough items.
export const isWCType = ({ type, variant, questionType, batches, answerVariants } = {}) => {
  const supported = WC_QUESTION_TYPES.includes(questionType) || WC_TYPES.includes(variant || type)

  return supported && WC_MIN_ITEMS <= (batches?.length || answerVariants?.length || 0)
}

// Whether the question can show respondent verbatims. The report-mode flags travel with the
// question rather than being read here, so this module stays free of page state.
export const isVerbatimType = ({ type, variant, questionId, questionType, isReportVirtual, isReportXpert } = {}) => {
  if (isReportVirtual || isReportXpert) return false

  const resolvedType = variant || type

  if (undefined !== questionId && !EXTENDED_QUESTION_ID_REG.test(questionId) && VERBATIM_TYPES.includes(resolvedType)) {
    return true
  }
  if (VERBATIM_TYPES.includes(questionType)) return true

  return VERBATIM_OWN_TYPES.includes(resolvedType)
}

// Filters target for a clicked answer label, ported from the ItemsListView base the question
// tables used to extend: which filter group, key and value the click stands for. The view
// performs the sidebar work.
// A question is addressed either by its position or by the id that position resolves to. Only a
// position needs converting; anything else already is the id, and asking for its position back
// finds no question at all.
export const resolveQuestionId = questionId => {
  if (null == questionId) return questionId

  const id = String(questionId).split('-')[0]

  return Number.isNaN(Number(id)) ? id : questionPositionToId(Number(id))
}

export const calcLabelClickTarget = ({ id, questionId, batch, answerVariants = [], position } = {}) => {
  let resolvedId = id
  let resolvedQuestionId = resolveQuestionId(questionId)

  // an extended-question label addresses the parent question, not the sub-question
  if (isCustomQuestion(resolvedQuestionId || resolvedId, 'xq')) {
    resolvedId = String(resolvedId).split('_')[0]
    resolvedQuestionId = String(resolvedQuestionId).split('_')[0]
  }

  const custom = isCustomQuestion(resolvedQuestionId || resolvedId)
  const group = custom ? 'virtualQuestions' : 'questions'
  const question = (custom && resolvedQuestionId) || questionId || resolvedId

  // The question the row hangs off, named the way the filters address it. A block that carries no
  // question of its own is its own question, so the key falls back to its id.
  const questionKey =
    resolvedQuestionId || (Number.isNaN(Number(resolvedId)) ? resolvedId : questionPositionToId(Number(resolvedId)))

  // a row of a sub-question addresses that sub-question, whichever of the two named it
  const baseKey = batch ? `${questionKey}SQ${batch.position}` : questionKey

  const answerVariant = answerVariants[position - 1] || {}

  const key = Object.hasOwn(answerVariant, 'type')
    ? ('sentiment' === answerVariant.type && `TAG.SENTIMENT.${String(baseKey).toUpperCase()}`) ||
      ('tag' === answerVariant.type && `TAG.${String(baseKey).toUpperCase()}`) ||
      ''
    : String(baseKey).toUpperCase()

  const value = Object.hasOwn(answerVariant, 'type') ? answerVariant.tag : `A${position}`

  return { group, question, key, value }
}
